// src/components/pages/profile/ProfileSettingsPage.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Grid,
  TextField,
  Typography,
  Alert,
  Avatar,
  Snackbar,
  IconButton,
  Button,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { ReactComponent as IconDelete } from "../../../assets/icons/icon-delete.svg";
import { getAuth } from "firebase/auth";
import {
  updateProfileInFirestore,
  updateProfileImageInAllCollections,
  checkUserNameAvailability,
  updateAzureUserProfileUsername,
  getOrCreateUserProfile,
} from "../../../services/profileService";
import useStore from "../../../store/useStore";
import useAuthStore from "../../../store/useAuthStore";
import ImageUploader from "../../profile/organisms/ImageUploader";

const ProfileSettingsPage = () => {
  const { userProfile, setUserProfile, clearUserProfile } = useStore();
  const { setUser } = useAuthStore();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    userName: "",
    displayName: "",
    bio: "",
    profileImage: "",
    email: "",
    phoneNumber: "",
  });
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [userNameError, setUserNameError] = useState(false);
  const [userNameDisabled, setUserNameDisabled] = useState(false);
  const [usernameAvailability, setUsernameAvailability] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Helper: Update username flags based on input (if it contains an "@" mark, assume it's an email)
  const updateUsernameFlags = (userName) => {
    if (userName.includes("@")) {
      setUserNameError(true);
      setUserNameDisabled(false);
    } else {
      setUserNameError(false);
      setUserNameDisabled(true);
    }
    console.log("userNameError: ", userNameError);
  };

  useEffect(() => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    setUser(currentUser);
    if (currentUser && !userProfile) {
      setLoadingProfile(true);
      getOrCreateUserProfile(currentUser)
        .then((profile) => {
          setUserProfile(profile);
          setFormData({
            userName: profile.userName || "",
            displayName: profile.displayName || "",
            bio: profile.bio || "",
            profileImage: profile.profileImage || "",
            email: userProfile.email || "",
            phoneNumber: userProfile.phoneNumber || "",
          });
          updateUsernameFlags(profile.userName || "");
        })
        .catch((error) => console.error("Error fetching profile:", error))
        .finally(() => setLoadingProfile(false));
    } else if (userProfile) {
      setFormData({
        userName: userProfile.userName || "",
        displayName: userProfile.displayName || "",
        bio: userProfile.bio || "",
        profileImage: userProfile.profileImage || "",
        email: userProfile.email || "",
        phoneNumber: userProfile.phoneNumber || "",
      });
      updateUsernameFlags(userProfile.userName || "");
    }
  }, [userProfile, setUserProfile, setUser]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "userName") {
      setCheckingUsername(true);
      // Ideally, use a debounced function for checking
      setTimeout(async () => {
        try {
          const isAvailable = await checkUserNameAvailability(value);
          setUsernameAvailability(isAvailable ? "available" : "taken");
        } catch (error) {
          console.error("Error checking username availability:", error);
          setUsernameAvailability("error");
        } finally {
          setCheckingUsername(false);
        }
      }, 500);
    }
  };

  const handleUpdateProfile = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!userProfile || !currentUser || !currentUser.uid) {
      console.error("User profile or UID is missing.");
      return;
    }
    try {
      if (formData.userName !== userProfile.userName) {
        await updateAzureUserProfileUsername(
          formData.userName,
          currentUser.email
        );
      }
      const updatedProfile = {
        ...userProfile,
        ...formData,
        uid: currentUser.uid,
      };
      await updateProfileInFirestore(currentUser.uid, updatedProfile);
      setUserProfile(updatedProfile);
      setSnackbarMessage("Profile updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setUserNameError(false);
      setUsernameAvailability(null);
      if (!updatedProfile.userName.includes("@")) {
        setUserNameDisabled(true);
      }
    } catch (error) {
      setSnackbarMessage("Error updating profile.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Error updating profile:", error);
    }
  };

  const handleImageUpload = async (imageUrl) => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!userProfile || !currentUser || !currentUser.uid) return;
    setFormData((prev) => ({ ...prev, profileImage: imageUrl }));
    const updatedProfile = {
      ...userProfile,
      profileImage: imageUrl,
      uid: currentUser.uid,
    };
    await updateProfileInFirestore(currentUser.uid, updatedProfile);
    setUserProfile(updatedProfile);
    // Update localStorage so TopAppBar shows the new profile image
    localStorage.setItem("userProfile", JSON.stringify(updatedProfile));
    await updateProfileImageInAllCollections(currentUser.uid, imageUrl);
  };

  const handleRemoveProfilePicture = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!userProfile || !currentUser || !currentUser.uid) {
      console.error("User profile or UID is missing.");
      return;
    }
    const imageUrl = "";
    setFormData((prev) => ({ ...prev, profileImage: imageUrl }));
    const updatedProfile = {
      ...userProfile,
      profileImage: imageUrl,
      uid: currentUser.uid,
    };
    await updateProfileInFirestore(currentUser.uid, updatedProfile);
    setUserProfile(updatedProfile);
    // Update localStorage so TopAppBar reflects the change
    localStorage.setItem("userProfile", JSON.stringify(updatedProfile));
    await updateProfileImageInAllCollections(currentUser.uid, imageUrl);
  };

  // const handleCancelEdit = () => navigate("/");
  const handleSignOut = async () => {
    try {
      const auth = getAuth();
      await auth.signOut();
      clearUserProfile();
      navigate("/");
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const textFieldStyles = (theme) => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: "50px", // Round edges
      border: `1px solid ${theme.palette.primary.main}`,
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  });

  if (loadingProfile || !userProfile) {
    return (
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="h6">Loading profile...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 3, pl: 5, pr: 5 }}>
      <Box>
        {/* Profile Picture Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Grid
            item
            xs={12}
            sm={12}
            sx={{ display: "flex", alignItems: "center", gap: 2 }}
          >
            <Avatar
              src={formData.profileImage}
              alt="Profile Image"
              sx={{
                width: { xs: 80, sm: 120 },
                height: { xs: 80, sm: 120 },
              }}
            />
            <ImageUploader
              onImageUpload={handleImageUpload}
              userId={userProfile.userId}
            />
            <IconButton
              onClick={handleRemoveProfilePicture}
              sx={{
                borderRadius: "50%", // ensures the button is circular
                width: 40,
                height: 40,
                backgroundColor: "red",
                padding: 0, // removes extra padding if needed
              }}
            >
              <IconDelete style={{ width: "20px", height: "20px" }} />
            </IconButton>
          </Grid>
        </Grid>

        {/* Your Name Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="body1" align="left" sx={{ fontWeight: 700 }}>
              Your Name
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              fullWidth
              name="displayName"
              value={formData.displayName}
              onChange={handleChange}
              variant="outlined"
              size="small"
              sx={(theme) => textFieldStyles(theme)}
            />
          </Grid>
        </Grid>

        {/* User Name Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="body1" align="left" sx={{ fontWeight: 700 }}>
              User Name
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              fullWidth
              name="userName"
              value={formData.userName}
              onChange={handleChange}
              disabled={userNameDisabled}
              variant="outlined"
              size="small"
              sx={(theme) => textFieldStyles(theme)}
            />
            {checkingUsername ? (
              <Alert variant="filled" severity="info" sx={{ mt: 1 }}>
                Checking username availability...
              </Alert>
            ) : usernameAvailability === "available" ? (
              <Alert variant="filled" severity="success" sx={{ mt: 1 }}>
                Username is available!
              </Alert>
            ) : usernameAvailability === "taken" ? (
              <Alert variant="filled" severity="error" sx={{ mt: 1 }}>
                Username is already taken.
              </Alert>
            ) : usernameAvailability === "error" ? (
              <Alert variant="filled" severity="warning" sx={{ mt: 1 }}>
                Error checking username. Please try again later.
              </Alert>
            ) : null}
          </Grid>
        </Grid>

        {/* Bio Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="body1" align="left" sx={{ fontWeight: 700 }}>
              Your Bio
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              fullWidth
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              variant="outlined"
              multiline
              rows={4}
              size="small"
              sx={(theme) => ({
                "& .MuiOutlinedInput-root": {
                  borderRadius: "25px", // Round edges
                  border: `1px solid ${theme.palette.primary.main}`,
                  "&:hover fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.primary.main,
                  },
                },
              })}
            />
          </Grid>
        </Grid>

        {/* Your Email Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="body1" align="left" sx={{ fontWeight: 700 }}>
              Your Email
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              fullWidth
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={true}
              variant="outlined"
              size="small"
              sx={(theme) => textFieldStyles(theme)}
            />
          </Grid>
        </Grid>

        {/* Your Email Section */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Grid item xs={12} sm={3}>
            <Typography variant="body1" align="left" sx={{ fontWeight: 700 }}>
              Your Phone Number
            </Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              fullWidth
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              disabled={true}
              variant="outlined"
              size="small"
              sx={(theme) => textFieldStyles(theme)}
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ textAlign: "center" }}>
          <Button
            variant="contained"
            onClick={handleUpdateProfile}
            sx={(theme) => ({
              flex: "none",
              textTransform: "none",
              borderRadius: "50px",
              fontWeight: 700,
              backgroundColor: theme.colors.blue,
              border: `1px solid ${theme.colors.blue}`,
              color: "white",
              pl: 3,
              pr: 3,
              pt: "10px",
              pb: "10px",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            })}
          >
            Save Changes
          </Button>
        </Box>

        {/* Sign Out Button */}
        <Box sx={{ mt: 2, mb: 2, textAlign: "center" }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleSignOut}
            sx={{
              flex: "none",
              textTransform: "none",
              textDecoration: "underline",
              fontWeight: 700,
              backgroundColor: "transparent",
              border: `0px`,
              color: "black",
              pl: 3,
              pr: 3,
              pt: "10px",
              pb: "10px",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            Sign Out
          </Button>
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <MuiAlert
            onClose={handleSnackbarClose}
            variant="filled"
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default ProfileSettingsPage;
